import React, { useMemo } from 'react';

const PATTERNS = [
  {
    id: 'meeting',
    keywords: ['calendar', 'schedule', 'meeting', 'call', 'appointment', 'availability', 'calendly', 'book a time', 'phone call', 'zoom', 'teams', 'introductory call', 'connect with you'],
    chips: [
      { label: 'Confirm availability', icon: '📅', text: 'Confirm I am available and will schedule the meeting' },
      { label: 'Request reschedule', icon: '🔄', text: 'Ask to reschedule to a different time' },
      { label: 'Decline meeting', icon: '🚫', text: 'Politely decline the meeting request' }
    ]
  },
  {
    id: 'job',
    keywords: ['opportunity', 'position', 'role', 'hiring', 'interview', 'resume', 'candidate', 'recruit', 'job', 'career', 'contractor', 'employment', 'apply', 'application'],
    chips: [
      { label: 'Express interest', icon: '🤝', text: 'Express strong interest in the opportunity and willingness to discuss further' },
      { label: 'Request details', icon: '📋', text: 'Ask for more details about the role, compensation, and expectations' },
      { label: 'Decline opportunity', icon: '👋', text: 'Politely decline the opportunity and wish them well' }
    ]
  },
  {
    id: 'congrats',
    keywords: ['congratulations', 'selected', 'winner', 'finalist', 'achievement', 'award', 'honored', 'proud', 'recognized', 'accepted', 'admission'],
    chips: [
      { label: 'Thank & confirm', icon: '🎉', text: 'Thank them for the wonderful news and confirm participation' },
      { label: 'Ask for details', icon: '❓', text: 'Thank them and ask for more details about next steps' },
      { label: 'Attached docs', icon: '📎', text: 'Thank them and mention I have attached the required signed documents' }
    ]
  },
  {
    id: 'action',
    keywords: ['please complete', 'deadline', 'required', 'submit', 'sign', 'return', 'fill out', 'action required', 'action items', 'by end of', 'asap', 'urgent'],
    chips: [
      { label: 'Confirm done', icon: '✅', text: 'Confirm I have completed the requested action' },
      { label: 'Attached docs', icon: '📎', text: 'Confirm and mention I have attached the required documents' },
      { label: 'Request extension', icon: '⏰', text: 'Ask for more time or an extension on the deadline' }
    ]
  },
  {
    id: 'followup',
    keywords: ['following up', 'checking in', 'touch base', 'any update', 'status', 'haven\'t heard', 'wanted to check', 'circling back', 'just checking'],
    chips: [
      { label: 'Provide update', icon: '📊', text: 'Provide a status update on the matter' },
      { label: 'Need more time', icon: '⏳', text: 'Acknowledge and ask for a bit more time' },
      { label: 'Confirm done', icon: '✅', text: 'Confirm the task is complete and share the outcome' }
    ]
  },
  {
    id: 'complaint',
    keywords: ['issue', 'problem', 'disappointed', 'complaint', 'resolve', 'wrong', 'error', 'broken', 'not working', 'frustrated', 'unacceptable', 'dissatisfied'],
    chips: [
      { label: 'Apologize & resolve', icon: '🙏', text: 'Apologize for the issue and offer a resolution' },
      { label: 'Ask for details', icon: '🔍', text: 'Ask for more details to investigate the issue' },
      { label: 'Escalate', icon: '📤', text: 'Acknowledge the issue and mention it has been escalated to the appropriate team' }
    ]
  },
  {
    id: 'sales',
    keywords: ['offer', 'discount', 'pricing', 'proposal', 'deal', 'demo', 'trial', 'subscription', 'package', 'quote', 'buy', 'purchase', 'product', 'service'],
    chips: [
      { label: 'Interested', icon: '👍', text: 'Express interest and ask for more information or a demo' },
      { label: 'Not interested', icon: '👎', text: 'Politely decline and say it is not a fit right now' },
      { label: 'Negotiate', icon: '💰', text: 'Express interest but ask about pricing flexibility or alternatives' }
    ]
  },
  {
    id: 'thankyou',
    keywords: ['thank you', 'thanks for', 'appreciate', 'grateful', 'great job', 'well done', 'excellent work', 'good work'],
    chips: [
      { label: 'You\'re welcome', icon: '😊', text: 'Say you\'re welcome and it was a pleasure to help' },
      { label: 'Happy to help', icon: '🤗', text: 'Express that you were happy to help and are available anytime' },
      { label: 'Return thanks', icon: '🙌', text: 'Thank them back for the opportunity and kind words' }
    ]
  },
  {
    id: 'invitation',
    keywords: ['invite', 'event', 'attend', 'rsvp', 'join us', 'participate', 'gathering', 'party', 'ceremony', 'reception', 'webinar', 'conference'],
    chips: [
      { label: 'Accept invitation', icon: '✅', text: 'Accept the invitation and confirm attendance' },
      { label: 'Decline politely', icon: '🙏', text: 'Politely decline the invitation due to a scheduling conflict' },
      { label: 'Ask for details', icon: '❓', text: 'Express interest and ask for more details about the event' }
    ]
  },
  {
    id: 'question',
    keywords: ['question', 'wondering', 'curious', 'could you explain', 'how do', 'what is', 'help me', 'can you', 'clarify', 'confused'],
    chips: [
      { label: 'Answer question', icon: '💡', text: 'Answer their question clearly and helpfully' },
      { label: 'Need clarification', icon: '🤔', text: 'Ask for clarification to better understand what they need' },
      { label: 'Direct to resource', icon: '📚', text: 'Point them to the right resource or person for help' }
    ]
  },
  {
    id: 'introduction',
    keywords: ['introduce myself', 'nice to meet', 'reaching out', 'first time', 'new to', 'getting started', 'onboarding', 'welcome aboard', 'welcome to the team'],
    chips: [
      { label: 'Warm introduction', icon: '👋', text: 'Respond warmly, introduce myself briefly, and express excitement to connect' },
      { label: 'Ask to connect', icon: '🔗', text: 'Thank them and suggest a time to meet or chat further' },
      { label: 'Share background', icon: '📝', text: 'Introduce myself with relevant background and experience' }
    ]
  },
  {
    id: 'payment',
    keywords: ['invoice', 'payment', 'billing', 'charge', 'receipt', 'refund', 'outstanding', 'overdue', 'balance', 'wire transfer', 'paid'],
    chips: [
      { label: 'Confirm payment', icon: '💳', text: 'Confirm the payment has been processed or will be sent' },
      { label: 'Request clarification', icon: '🧾', text: 'Ask for clarification on the invoice or billing details' },
      { label: 'Dispute charge', icon: '⚠️', text: 'Politely dispute the charge and request a review' }
    ]
  }
];

function detectPatterns(text) {
  if (!text || text.trim().length < 20) return [];

  const lower = text.toLowerCase();
  const matched = [];

  for (const pattern of PATTERNS) {
    const matchCount = pattern.keywords.filter(kw => lower.includes(kw)).length;
    if (matchCount > 0) {
      matched.push({ ...pattern, score: matchCount });
    }
  }

  // Sort by match strength, return top 2 categories
  matched.sort((a, b) => b.score - a.score);
  return matched.slice(0, 2);
}

export default function SuggestionChips({ message, onSelect, selectedChips }) {
  const suggestions = useMemo(() => detectPatterns(message), [message]);

  if (suggestions.length === 0) return null;

  // Flatten all chips from matched categories
  const allChips = suggestions.flatMap(s => s.chips);

  return (
    <div style={styles.container}>
      <span style={styles.label}>Quick replies:</span>
      <div style={styles.chips}>
        {allChips.map(chip => {
          const isSelected = selectedChips.includes(chip.text);
          return (
            <button
              key={chip.label}
              onClick={() => onSelect(chip.text, !isSelected)}
              style={{
                ...styles.chip,
                ...(isSelected ? styles.chipActive : {})
              }}
            >
              <span>{chip.icon}</span>
              <span>{chip.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    flexWrap: 'wrap'
  },
  label: {
    fontSize: 12,
    color: 'var(--text-muted)',
    fontWeight: 500,
    marginTop: 6,
    whiteSpace: 'nowrap'
  },
  chips: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap'
  },
  chip: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '5px 10px',
    background: 'var(--bg-primary)',
    border: '1px solid var(--border)',
    borderRadius: 16,
    color: 'var(--text-secondary)',
    fontSize: 12,
    cursor: 'pointer',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap'
  },
  chipActive: {
    background: '#6366f122',
    borderColor: 'var(--accent)',
    color: 'var(--accent)'
  }
};
